import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { apiEndpoints, handleApiError } from '../services/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  RefreshCw,
  DollarSign,
  Calendar,
  FileText,
  CreditCard,
  Users,
  UserCheck,
  Eye,
  Building,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  Clock,
  Filter
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PersonnelPaymentManagement = () => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [showPersonnelModal, setShowPersonnelModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [personnelFilter, setPersonnelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

    const [refreshKey, setRefreshKey] = useState(0);
// Form state
  const [formData, setFormData] = useState({
    personnelId: '',
    paymentType: 'Salary',
    description: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
    baseSalary: '',
    advanceAmount: '',
    bonusAmount: '',
    paymentMethod: 'Bank Transfer',
    referenceNumber: '',
    status: 'Pending'
  });

  const paymentTypes = [
    { value: 'Salary', label: t('common.salary') },
    { value: 'Advance', label: t('common.advance') },
    { value: 'Bonus', label: t('common.bonus') },
    { value: 'Overtime', label: t('common.overtime') },
    { value: 'Commission', label: t('common.commission') },
    { value: 'Other', label: t('common.other') }
  ];

  const paymentMethods = [
    { value: 'Bank Transfer', label: t('common.bankTransfer') },
    { value: 'Cash', label: t('common.cash') },
    { value: 'Check', label: t('common.check') },
    { value: 'Other', label: t('common.other') }
  ];

  const statusOptions = [
    { value: 'Pending', label: t('common.pending') },
    { value: 'Processed', label: t('common.completed') },
    { value: 'Cancelled', label: t('common.cancelled') }
  ];

  const statusFilterOptions = [
    { value: '', label: t('common.allStatus') },
    { value: 'Pending', label: t('common.pending') },
    { value: 'Processed', label: t('common.processed') },
    { value: 'Cancelled', label: t('common.cancelled') }
  ];

  useEffect(() => {
    fetchPayments();
    fetchPersonnel();
  }, [currentPage, itemsPerPage, searchTerm, typeFilter, personnelFilter, statusFilter]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      
      if (searchTerm) params.search = searchTerm;
      if (typeFilter) params.type = typeFilter;
      if (personnelFilter) params.personnelId = personnelFilter;
      
      const response = await apiEndpoints.personnelPayments.getPaginated(params);
      
      setPayments(response.data.payments || []);
      setTotalPages(response.data.totalPages || 0);
      setTotalItems(response.data.totalItems || 0);
    } catch (error) {
      console.error('Error fetching payments:', error);
      handleApiError(error, 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const [coachesResponse, adminsResponse] = await Promise.all([
        apiEndpoints.users.getByRole('Coach'),
        apiEndpoints.users.getByRole('Admin')
      ]);

      const allPersonnel = [
        ...(coachesResponse.data?.users || []).map(user => ({ ...user, role: 'Coach' })),
        ...(adminsResponse.data?.users || []).map(user => ({ ...user, role: 'Admin' }))
        // Exclude SuperAdmin users from personnel list
      ];
      
      setPersonnel(allPersonnel);
    } catch (error) {
      console.error('Error fetching personnel:', error);
      handleApiError(error, 'Failed to load personnel');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const paymentData = {
        ...formData,
        amount: parseFloat(formData.amount),
        baseSalary: formData.baseSalary ? parseFloat(formData.baseSalary) : null,
        advanceAmount: formData.advanceAmount ? parseFloat(formData.advanceAmount) : null,
        bonusAmount: formData.bonusAmount ? parseFloat(formData.bonusAmount) : null,
        personnelId: parseInt(formData.personnelId)
      };

      if (editingPayment) {
        await apiEndpoints.personnelPayments.update(editingPayment.id, paymentData);
        toast.success(t('common.paymentUpdatedSuccessfully'));
      } else {
        await apiEndpoints.personnelPayments.create(paymentData);
        toast.success(t('common.paymentCreatedSuccessfully'));
      }

      setShowModal(false);
      setEditingPayment(null);
      resetForm();
      fetchPayments();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error saving payment:', error);
      handleApiError(error, 'Failed to save payment');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      personnelId: payment.personnelId.toString(),
      paymentType: payment.paymentType,
      description: payment.description,
      amount: payment.amount.toString(),
      paymentDate: payment.paymentDate.split('T')[0],
      notes: payment.notes || '',
      baseSalary: payment.baseSalary?.toString() || '',
      advanceAmount: payment.advanceAmount?.toString() || '',
      bonusAmount: payment.bonusAmount?.toString() || '',
      paymentMethod: payment.paymentMethod || 'Bank Transfer',
      referenceNumber: payment.referenceNumber || '',
      status: payment.status === 0 ? 'Pending' : payment.status === 1 ? 'Processed' : 'Cancelled'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await apiEndpoints.personnelPayments.delete(id);
        toast.success(t('common.paymentDeletedSuccessfully'));
        fetchPayments();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
        console.error('Error deleting payment:', error);
        handleApiError(error, 'Failed to delete payment');
      }
    }
  };

  const handleProcess = async (id) => {
    try {
      await apiEndpoints.personnelPayments.markAsCompleted(id);
      toast.success(t('common.paymentProcessedSuccessfully'));
      fetchPayments();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error processing payment:', error);
      handleApiError(error, 'Failed to process payment');
    }
  };

  const handleCancel = async (id) => {
    try {
      await apiEndpoints.personnelPayments.cancel(id);
      toast.success(t('common.paymentCancelledSuccessfully'));
      fetchPayments();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error cancelling payment:', error);
      handleApiError(error, 'Failed to cancel payment');
    }
  };

  const resetForm = () => {
    setFormData({
      personnelId: '',
      paymentType: 'Salary',
      description: '',
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      notes: '',
      baseSalary: '',
      advanceAmount: '',
      bonusAmount: '',
      paymentMethod: 'Bank Transfer',
      referenceNumber: '',
      status: 'Pending'
    });
  };

  const handleNewPayment = () => {
    setEditingPayment(null);
    resetForm();
    setShowModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Processed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processed':
        return 'bg-green-100 text-green-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <>
      <div className="flex flex-col h-full" key={refreshKey}>
        {/* Fixed Header Section */}
        <div className="flex-shrink-0">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('common.personnelPayments')}</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">{t('common.manageStaffPayments')}</p>
            </div>
            <button
              onClick={handleNewPayment}
              className="bg-blue-600 text-white px-3 py-2 sm:px-4 rounded-lg hover:bg-blue-700 flex items-center gap-2 min-h-[44px] min-w-[44px] touch-manipulation text-sm sm:text-base"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t('common.addPayment')}</span>
            </button>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.search')}
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('common.searchPayments')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.paymentType')}
                </label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('common.allTypes')}</option>
                  {paymentTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Personnel Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.personnel')}
                </label>
                <select
                  value={personnelFilter}
                  onChange={(e) => setPersonnelFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('common.allPersonnel')}</option>
                  {personnel.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.firstName} {person.lastName} ({person.role === 'Coach' ? t('common.coach') : person.role === 'Admin' ? t('common.admin') : person.role})
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.status')}
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {statusFilterOptions.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setTypeFilter('');
                    setPersonnelFilter('');
                    setStatusFilter('');
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  {t('common.clearFilters')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content Section */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
          {/* Payments Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.personnel')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.type')}
                </th>
<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.description')}
                </th>
<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.date')}
                </th>
<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
                      <span className="ml-2">{t('common.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
{t('common.noPaymentsFound')}
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.personnelName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {payment.personnelEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {payment.paymentType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{payment.description}</div>
                      {payment.notes && (
                        <div className="text-sm text-gray-500">{payment.notes}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ${payment.amount.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(payment.paymentDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                        {getStatusIcon(payment.status)}
                        <span className="ml-1">{payment.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(payment)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {payment.status === 'Pending' && (
                          <>
                            <button
                              onClick={() => handleProcess(payment.id)}
                              className="text-green-600 hover:text-green-900"
                              title={t('common.processPayment')}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleCancel(payment.id)}
                              className="text-red-600 hover:text-red-900"
                              title={t('common.cancelPayment')}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(payment.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </div>

        {/* Pagination - Sticky at bottom */}
        <div className="sticky bottom-0 bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 z-10 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-700 font-medium">
              {t('common.showing')} <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> {t('common.to')} <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> {t('common.of')} <span className="font-bold text-blue-600">{totalItems}</span> {t('common.results')}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1 || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                {t('common.first')}
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1 || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                {t('common.previous')}
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                {t('common.next')}
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                {t('common.last')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
{editingPayment ? t('common.editPayment') : t('common.addNewPayment')}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Personnel Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
{t('common.personnel')} *
                    </label>
                    <select
                      value={formData.personnelId}
                      onChange={(e) => setFormData({ ...formData, personnelId: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t('common.selectPersonnel')}</option>
                      {personnel.map(person => (
                        <option key={person.id} value={person.id}>
{person.firstName} {person.lastName} ({person.role === 'Coach' ? t('common.coach') : person.role === 'Admin' ? t('common.admin') : person.role})
                        </option>
                      ))}
                    </select>
                  </div>

{/* Payment Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('common.paymentType')} *
                    </label>
                    <select
                      value={formData.paymentType}
                      onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {paymentTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

{/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('common.amount')} *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

{/* Payment Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('common.paymentDate')} *
                    </label>
                    <input
                      type="date"
                      value={formData.paymentDate}
                      onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

{/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('common.status')}
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>

{/* Base Salary (for Salary type) */}
                  {formData.paymentType === 'Salary' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('common.baseSalary')}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.baseSalary}
                        onChange={(e) => setFormData({ ...formData, baseSalary: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}

{/* Advance Amount (for Advance type) */}
                  {formData.paymentType === 'Advance' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('common.advanceAmount')}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.advanceAmount}
                        onChange={(e) => setFormData({ ...formData, advanceAmount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}

{/* Bonus Amount (for Bonus type) */}
                  {formData.paymentType === 'Bonus' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('common.bonusAmount')}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.bonusAmount}
                        onChange={(e) => setFormData({ ...formData, bonusAmount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}

{/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('common.paymentMethod')}
                    </label>
                    <select
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {paymentMethods.map(method => (
                        <option key={method.value} value={method.value}>{method.label}</option>
                      ))}
                    </select>
                  </div>

{/* Reference Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('common.referenceNumber')}
                    </label>
                    <input
                      type="text"
                      value={formData.referenceNumber}
                      onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

{/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.description')} *
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

{/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.notes')}
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
{t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
{editingPayment ? t('common.updatePayment') : t('common.createPayment')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PersonnelPaymentManagement;
